import validator from 'validator';

const PERIOD = {
  AM: 'AM',
  PM: 'PM'
};

const HOUR_FORMAT = 12;

const DEFAULT_TIME = '9:00';

/**
 * Converts a 24-hour format time string (e.g., "18:00") to a 12-hour format time string with AM/PM (e.g., "6:00 PM").
 * @param {string} time24h - The time string in 24-hour format ("HH:MM").
 * @returns {string} - The time formatted in 12-hour format as "h:mm AM/PM".
 * @example
 * convertTo12Hour("18:00"); // Returns "6:00 PM"
 * convertTo12Hour("09:30"); // Returns "9:30 AM"
 */
export function convertTo12Hour(time24h: string): string {
  let timeInput = time24h;
  // Validate input format: "HH:MM" where HH is 00-23 and MM is 00-59
  const isValidTime24h = validator.isTime(time24h);

  if (!isValidTime24h) {
    timeInput = DEFAULT_TIME;
  }
  const time = timeInput.padStart(5, '0');
  // Create a new Date object with the 24-hour time
  const date = new Date(`1970-01-01T${time}`);
  // Format it using Intl.DateTimeFormat
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true // Forces 12-hour format
  });

  return formatter.format(date);
}

/**
 * Converts a 12-hour format time string with AM/PM (e.g., "6:00 PM") to a 24-hour format time string (e.g., "18:00").
 * @param {string} time12h - The time string in 12-hour format with AM/PM notation (e.g., "h:mm AM/PM").
 * @returns {string} - The time formatted in 24-hour format as "HH:MM".
 * @example
 * convertTo24Hour("6:00 PM"); // Returns "18:00"
 * convertTo24Hour("12:30 AM"); // Returns "00:30"
 */
export function convertTo24Hour(time12h: string): string {
  const [time, period] = time12h.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let adjustedHours = hours;

  // Create a date at midnight
  const date = new Date();

  // Adjust hours for AM/PM
  if (period.toUpperCase() === PERIOD.PM && adjustedHours !== HOUR_FORMAT) {
    adjustedHours += HOUR_FORMAT;
  } else if (
    period.toUpperCase() === PERIOD.AM &&
    adjustedHours === HOUR_FORMAT
  ) {
    adjustedHours = 0;
  }

  date.setHours(adjustedHours, minutes);

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(date);
}
